import { Task } from '../types';

/**
 * Pure, side-effect-free helpers for task dependencies and sub-tasks.
 *
 * These are intentionally free of any Firebase/Firestore imports so they can be
 * unit-tested in isolation and reused across the (large) Tasks UI without
 * pulling in service-layer dependencies. `tasksService` delegates to these.
 */

/** A task is blocked when at least one of its dependencies is not yet completed. */
export function isBlocked(task: Task, allTasks: Task[]): boolean {
  return getBlockingTasks(task, allTasks).length > 0;
}

/** The (still incomplete) tasks that must be finished before `task` can be done. */
export function getBlockingTasks(task: Task, allTasks: Task[]): Task[] {
  if (!task.dependsOn || task.dependsOn.length === 0) return [];
  return task.dependsOn
    .map(depId => allTasks.find(t => t.id === depId))
    .filter((t): t is Task => !!t && !t.completed);
}

/** Tasks that declare `task` as one of their dependencies (i.e. `task` blocks them). */
export function getBlockedByThis(task: Task, allTasks: Task[]): Task[] {
  return allTasks.filter(t => t.dependsOn?.includes(task.id));
}

/** Completion progress of a task's direct sub-tasks. */
export function getSubTaskProgress(task: Task, allTasks: Task[]): { completed: number; total: number } {
  if (!task.subtaskIds || task.subtaskIds.length === 0) return { completed: 0, total: 0 };
  const subTasks = task.subtaskIds
    .map(id => allTasks.find(t => t.id === id))
    .filter((t): t is Task => !!t);
  return {
    completed: subTasks.filter(t => t.completed).length,
    total: subTasks.length,
  };
}

/**
 * Would adding `targetDepId` as a dependency of `taskId` create a cycle?
 * Walks the dependency graph starting from the prospective dependency and
 * reports a cycle if it can reach `taskId` again.
 */
export function hasCircularDependency(taskId: string, targetDepId: string, allTasks: Task[]): boolean {
  const visited = new Set<string>();
  const reachesTask = (currentId: string): boolean => {
    if (currentId === taskId) return true; // cycle: we got back to the origin task
    if (visited.has(currentId)) return false;
    visited.add(currentId);
    const current = allTasks.find(t => t.id === currentId);
    if (!current?.dependsOn) return false;
    return current.dependsOn.some(depId => reachesTask(depId));
  };
  return reachesTask(targetDepId);
}

export type DependencyRejection = 'self' | 'duplicate' | 'cycle';

export type DependencyValidation =
  | { ok: true }
  | { ok: false; reason: DependencyRejection; message: string };

/** Validate that `depId` may be added as a dependency of `taskId`. */
export function validateDependency(taskId: string, depId: string, allTasks: Task[]): DependencyValidation {
  if (taskId === depId) {
    return { ok: false, reason: 'self', message: 'Eine Aufgabe kann nicht von sich selbst abhängen.' };
  }
  const task = allTasks.find(t => t.id === taskId);
  if (task?.dependsOn?.includes(depId)) {
    return { ok: false, reason: 'duplicate', message: 'Diese Abhängigkeit besteht bereits.' };
  }
  if (hasCircularDependency(taskId, depId, allTasks)) {
    return {
      ok: false,
      reason: 'cycle',
      message: 'Zirkuläre Abhängigkeit erkannt! Diese Verknüpfung würde eine Endlosschleife erzeugen.',
    };
  }
  return { ok: true };
}

/**
 * Whether `task` may be marked as completed. A task can only be completed once
 * all of its dependencies are done. Already-completed tasks are always allowed
 * (so they can be toggled back).
 */
export function canCompleteTask(task: Task, allTasks: Task[]): boolean {
  if (task.completed) return true;
  return getBlockingTasks(task, allTasks).length === 0;
}
